import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Close, SearchOutlined, ViewList, ViewModule } from '@mui/icons-material';
import { VisuallyHiddenInput } from 'ui-component/system/VisuallyHiddenInput';
import { enqueueSnackbar } from 'notistack';
import debounce from 'lodash/debounce';
import { getFileLinkFromServer } from 'utils/helpers';
import { MediaFile, MediaFileType } from 'types/files';
import FilesTableView from './FilesTableView';
import FilesGalleryView from './FilesGalleryView';
import useLocalization from 'hooks/useLocalization';
import { fetcher } from 'utils/axios';
import { useSelector } from 'store';
import Loader from 'ui-component/Loader';

const FILES_COUNT = 30;

interface FilesListLayoutProps {
  type: MediaFileType;
  noActions?: boolean;
  onClickAction?: (file: MediaFile) => void;
}

type CreateEditFileParams = {
  Id?: number;
  Name: string;
  Link: string | null;
  Category: MediaFileType;
  Status: 'Active' | 'Removed' | null;
  CompanyId: string;
  FunnerUserId: string;
};

const FilesListLayout: React.FC<FilesListLayoutProps> = ({
  type,
  noActions = false,
  onClickAction = () => {},
}) => {
  const [view, setView] = useState('gallery');
  const acceptUploadByType = {
    image: 'image/*',
    video: 'video/*',
    document: '.pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx',
  };
  const { currentLocale } = useLocalization();
  const user = useSelector((state) => state.user);
  const { companyId, FunnerUserId } = React.useMemo(() => {
    return {
      companyId: user?.CompanyId || '',
      FunnerUserId: user?.Udid || '',
    };
  }, [user]);

  const localization = React.useMemo(() => currentLocale.settingsPopup.uploadFiles, [currentLocale]);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [allFiles, setAllFiles] = useState<MediaFile[]>([]);
  
  // Debug files state changes
  React.useEffect(() => {
    console.log('📝 Files state changed:', files.length, 'files');
    console.log('📝 Files:', files);
  }, [files]);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileInfo, setFileInfo] = useState<{
    Id: number | null;
    Name: string;
    Link: string | null;
  }>({
    Id: null,
    Name: '',
    Link: null,
  });
  const [filesListFilters, setFilesListFilters] = useState<{
    from: string | null;
    to: string | null;
    searchTermName: string;
  }>({
    from: null,
    to: null,
    searchTermName: '',
  });
  const [filesListLoading, setFilesListLoading] = useState(false);
  
  // Debug loading state changes
  React.useEffect(() => {
    console.log('⏳ Loading state changed:', filesListLoading);
  }, [filesListLoading]);

  //* Filter files locally based on search term
  const filteredFiles = React.useMemo(() => {
    if (!filesListFilters.searchTermName) {
      return allFiles;
    }
    
    console.log('🔍 Filtering files locally with:', filesListFilters.searchTermName);
    const filtered = allFiles.filter(file => 
      file.Name?.toLowerCase().includes(filesListFilters.searchTermName.toLowerCase())
    );
    console.log('🔍 Filtered results:', filtered.length, 'files');
    return filtered;
  }, [allFiles, filesListFilters.searchTermName]);

  // Update files when filteredFiles changes
  React.useEffect(() => {
    setFiles(filteredFiles);
  }, [filteredFiles]);

  const [isFileNameEdit, setIsFileNameEdit] = useState(false);

  const fileUploadInputRef = useRef(null);
  const pageOffsetRef = useRef(1);

  const handleFileInfo = (params: Partial<typeof fileInfo>) => {
    setFileInfo((prevData) => ({ ...prevData, ...params }));
  };
  const handleCloseFileNameEdit = (clear = true) => {
    setIsFileNameEdit(false);
    if (clear) {
      setTimeout(() => {
        handleClearFileInfo();
      }, 300);
    }
  };
  const handleClearFileInfo = () => {
    setFileInfo({
      Id: null,
      Name: '',
      Link: null,
    });
  };
  const handleClearUI = React.useCallback((keepFilters = false) => {
    console.log('🧹 Clearing UI - keepFilters:', keepFilters);
    setFiles([]);
    setAllFiles([]);
    handleClearFileInfo();
    if (!keepFilters) {
      setFilesListFilters({
        from: null,
        to: null,
        searchTermName: '',
      });
    }
  }, []);

  const onEditExistingNameClick = (file: MediaFile) => {
    handleFileInfo({
      Id: file.Id,
      Name: file.Name,
      Link: file.Link,
    });
    setIsFileNameEdit(true);
  };
  const handleFilesListFilters = (
    params: Partial<typeof filesListFilters>,
    handleType: 'searchTerm' | null = null,
  ) => {
    const newFilters = { ...filesListFilters, ...params };
    console.log('🔍 Search filters:', newFilters);
    pageOffsetRef.current = 1;
    setFilesListFilters(newFilters);
    
    // For search term, just update filters - filtering is done locally
    // For other filters (date range etc), call API
    if (handleType !== 'searchTerm') {
      getMediaGalleryByCategory(newFilters);
    }
  };
  function handleLoadFilesListOnScroll(e: React.UIEvent<HTMLElement>, offset = 150) {
    const scrollContainer = e.currentTarget;
    const isAtBottom =
      scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - offset;

    if (isAtBottom) {
      // offsetContactsPage++;
      onScrollEndReached();
    }
  }
  const onScrollEndReached = () => {
    if (!filesListLoading && files.length / pageOffsetRef.current === FILES_COUNT) {
      console.log('query on scroll');
      pageOffsetRef.current += 1;
      getMediaGalleryByCategory(null, false);
    }
  };

  //! Fetch functions
  const getMediaGalleryByCategory = React.useCallback(
    async (directFilters: typeof filesListFilters | null = null, loader = true) => {
      try {
        if (loader) {
          setFilesListLoading(true);
        }
        // Always clear UI when loading new data (but keep filters)
        handleClearUI(true);

        const filters = directFilters || filesListFilters;

        const params = {
          category: type,
          page: 1,
          number: FILES_COUNT * pageOffsetRef.current,
          from: filters.from,
          to: filters.to,
          // Note: removed searchTermName since we're filtering locally
        };
        console.log('📡 API Request params:', params);
        
        const data = await fetcher(
          'get',
          ['/api/mediaGallery/getMediaGalleryByCategory', { data: params }],
          'history',
        );
        console.log('📡 API Response:', data);
        if (data?.result) {
          console.log('✅ Setting allFiles:', data.gallery);
          setAllFiles(data.gallery as MediaFile[]);
        }
      } catch (error) {
        console.error('getMediaGalleryByCategory error: ', error);
      } finally {
        if (loader) {
          console.log('🏁 Setting loading to false');
          setFilesListLoading(false);
        }
      }
    },
    [filesListFilters, handleClearUI, type],
  );

  const checkFileSize = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      //? enqueueSnackbar('No file were uploaded', { variant: 'error' });
      return false;
    }
    const currentSizeLimit = { image: 5, video: 15, document: 100 }[type];
    if (currentSizeLimit * 1024 * 1024 < file?.size) {
      return false;
    } else return true;
  };
  async function onUploadClick(e: React.ChangeEvent<HTMLInputElement>) {
    if (!checkFileSize(e)) {
      e.target.value = '';
      return enqueueSnackbar(
        <span
          dangerouslySetInnerHTML={{
            __html: localization.filesSizesLimits[type],
          }}
        ></span>,
        {
          variant: 'error',
        },
      );
    }
    setFileLoading(true);
    const uploadedFile = await getFileLinkFromServer(e);
    setFileLoading(false);
    if (uploadedFile) {
      handleFileInfo(uploadedFile);
      setIsFileNameEdit(true);
    } else {
      enqueueSnackbar('שגיאה בהעלאת הקובץ, נסה שוב', {
        variant: 'error',
      });
    }
  }
  async function saveUpdateItemInMediaGallery(params: CreateEditFileParams) {
    try {
      const res = await fetcher('post', ['/api/mediaGallery/saveUpdate', { data: params }], 'history');
      console.log('💾 saveUpdateItemInMediaGallery response:', res);
      console.log('💾 res?.data?.result:', res?.data?.result);
      console.log('💾 res?.result:', res?.result);
      
      // Try both possible response structures
      if (res?.data?.result || res?.result) {
        return true;
      } else {
        console.warn('💾 Unexpected response structure:', res);
        return false;
      }
    } catch (error) {
      console.error('saveUpdateItemInMediaGallery error: ', error);
      return false;
    }
  }
  async function handleUploadMediaToGallery(
    editedParams: Partial<CreateEditFileParams> = {},
    handleType = 'upload',
  ) {
    try {
      handleCloseFileNameEdit(false);
      const params: CreateEditFileParams = {
        Name: fileInfo.Name,
        Link: fileInfo.Link,
        Category: type,
        Status: 'Active',
        CompanyId: companyId,
        FunnerUserId: FunnerUserId,
        ...editedParams,
      };
      const res = await saveUpdateItemInMediaGallery(params);
      if (res) {
        // הצג הודעת הצלחה ספציפית לפי סוג הפעולה
        const successMessage = handleType === 'upload' ? 'הקובץ הועלה בהצלחה' : 'הקובץ עודכן בהצלחה';
        enqueueSnackbar(successMessage, { variant: 'success' });
        
        if (handleType === 'upload') {
          await getMediaGalleryByCategory(); // ← חיכוי ל-Promise
        } else if (handleType === 'update') {
          setFiles((prevFiles) => {
            const newFiles = [...prevFiles];
            const currentFile = newFiles.find((item) => item.Id === editedParams.Id);
            if (currentFile) {
              currentFile.Name = params.Name;
              currentFile.Status = params.Status;
            }
            return newFiles;
          });
        }
      } else {
        const errorMessage = handleType === 'upload' ? 'שגיאה בהעלאת הקובץ, נסה שוב' : 'שגיאה בעדכון הקובץ, נסה שוב';
        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      }
    } catch (error) {
      console.warn('handleUploadMediaToGallery error:', error);
      const errorMessage = handleType === 'upload' ? 'שגיאה בהעלאת הקובץ, נסה שוב' : 'שגיאה בעדכון הקובץ, נסה שוב';
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
    } finally {
      handleClearFileInfo();
    }
  }
  const handleDeleteFileFromGallery = React.useCallback(
    async (file: MediaFile) => {
      try {
        const params: CreateEditFileParams = {
          Id: file.Id,
          Name: file.Name,
          Link: file.Link,
          Category: type,
          Status: 'Removed',
          CompanyId: companyId,
          FunnerUserId: FunnerUserId,
        };
        const res = await saveUpdateItemInMediaGallery(params);
        console.log('🗑️ Delete result:', res);
        if (res) {
          enqueueSnackbar('הקובץ נמחק בהצלחה', {
            variant: 'success',
          });
          // Reload the files list after successful deletion
          await getMediaGalleryByCategory();
        } else {
          enqueueSnackbar('שגיאה במחיקת הקובץ, נסה שוב', {
            variant: 'error',
          });
        }
      } catch (error) {
        console.error('handleDeleteFileFromGallery error: ', error);
        enqueueSnackbar('שגיאה במחיקת הקובץ, נסה שוב', {
          variant: 'error',
        });
      } finally {
        handleClearFileInfo();
      }
    },
    [FunnerUserId, companyId, type, getMediaGalleryByCategory],
  );

  useEffect(() => {
    getMediaGalleryByCategory();
  }, [type]);
  // }, [getMediaGalleryByCategory]);

  return (
    <Fragment>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          height: '100%',
          flexWrap: 'nowrap',
          position: 'relative',
        }}
      >
        <Box
          width={'100%'}
          sx={{
            px: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            paddingBottom: '16px',
          }}
        >
          {filesListLoading && <Loader />}
          <Grid container spacing={2} alignItems={'center'} justifyContent={'space-between'}>
            <Grid size="auto" sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                label={localization.search}
                value={filesListFilters.searchTermName}
                sx={{ minWidth: 200, width: 250 }}
                onChange={(e) => {
                  handleFilesListFilters(
                    {
                      searchTermName: e.target.value,
                    },
                    'searchTerm',
                  );
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {filesListFilters.searchTermName.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleFilesListFilters({
                              searchTermName: '',
                            })
                          }
                        >
                          <Close color="error" sx={{ fontSize: 20 }} />
                        </IconButton>
                      )}
                      <SearchOutlined sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <ButtonGroup variant="outlined">
                <Button
                  disableElevation
                  variant={view === 'list' ? 'contained' : 'outlined'}
                  onClick={() => setView('list')}
                >
                  <ViewList />
                </Button>
                <Button
                  disableElevation
                  variant={view === 'gallery' ? 'contained' : 'outlined'}
                  onClick={() => setView('gallery')}
                >
                  <ViewModule />
                </Button>
              </ButtonGroup>
            </Grid>
            <Grid size="auto">
              {!noActions && (
                <Button
                  variant="contained"
                  disabled={fileLoading}
                  component="label"
                >
                  {localization.upload}
                  <VisuallyHiddenInput
                    type="file"
                    onChange={(e) => onUploadClick(e)}
                    ref={fileUploadInputRef}
                    accept={acceptUploadByType[type]}
                  />
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box
          width={'100%'}
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            minHeight: 272,
          }}
        >
          {!filesListLoading &&
            (view === 'list' ? (
              <FilesTableView
                files={files}
                handleDeleteFileFromGallery={handleDeleteFileFromGallery}
                onEditExistingNameClick={onEditExistingNameClick}
                onScrollEndReached={onScrollEndReached}
                noActions={noActions}
                onClickAction={onClickAction}
              />
            ) : (
              <FilesGalleryView
                files={files}
                type={type}
                handleDeleteFileFromGallery={handleDeleteFileFromGallery}
                onEditExistingNameClick={onEditExistingNameClick}
                handleLoadFilesListOnScroll={handleLoadFilesListOnScroll}
                noActions={noActions}
                onClickAction={onClickAction}
              />
            ))}
        </Box>
        {/* {filesListLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255, 255, 255, 0.5)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={60} color="secondary" />
          </Box>
        )} */}
      </Box>
      <Dialog open={isFileNameEdit} onClose={() => handleCloseFileNameEdit()} maxWidth="xs" fullWidth>
        <DialogTitle>{localization.editNamePopup.title}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <TextField
              autoFocus
              label={localization.editNamePopup.fileName}
              fullWidth
              value={fileInfo.Name}
              onChange={(e) => handleFileInfo({ Name: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseFileNameEdit()}>{localization.editNamePopup.cancel}</Button>
          <Button
            onClick={() =>
              handleUploadMediaToGallery(
                fileInfo.Id ? { Id: fileInfo.Id } : {},
                fileInfo.Id ? 'update' : 'upload',
              )
            }
            variant="contained"
          >
            {localization.editNamePopup.submit}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default FilesListLayout;
